<template>
  <rest-list :payload="listEndpoint" ref="list">
    <rest-list-column
      v-if="!campaignId"
      prop="campaign_title"
      :title="$t('Action_Field_Campaign')"
      :min-width="300"
      :tooltip="true"
    />

    <rest-list-column-date
      prop="create_time"
      :title="$t('Action_Field_CreateTime')"
    />

    <rest-list-column
      prop="error"
      :title="$t('Action_Field_Warning')"
      :filter-input="false"
      :width="100"
      align="center"
    >
      <template v-slot:default="scope">
        <rest-list-action
          v-if="scope.row.error"
          icon="el-icon-warning"
          :class="errorClass(scope.row)"
          :route="rowViewRoute(scope.row)"
          :title="errorMessage(scope.row)"
        />
      </template>
    </rest-list-column>

    <rest-list-column-enum
      prop="message_type"
      :title="$t('Action_Field_MessageType')"
      :enum="MessageTypeLabel"
      :min-width="150"
    />

    <rest-list-column-enum
      prop="application_id"
      :title="$t('Action_Field_Application')"
      :enum="applicationsList"
      :translate="false"
      :min-width="150"
    />

    <rest-list-column-enum
      prop="environment"
      :title="$t('Action_Field_Environment')"
      :enum="PushEnvironmentLabel"
      :min-width="150"
    />

    <rest-list-column-enum
      prop="visible_status"
      :title="$t('Action_Field_Status')"
      :enum="ActionStatusLabel"
      :min-width="150"
      :sortable="false"
    />

    <rest-list-column-number
      prop="target_count"
      :title="$t('Action_Field_TargetCount')"
      :formatter="targetCountFormatter"
      :min-width="100"
    />

    <rest-list-column-number
      prop="sent_count"
      :title="$t('Action_Field_SentCount')"
      :min-width="100"
    />

    <rest-list-column-number
      prop="open_count"
      :title="$t('Action_Field_OpensCount')"
      :min-width="100"
    />

    <rest-list-column-number
      prop="processing_count"
      :title="$t('Action_Field_ProcessingCount')"
      :min-width="100"
    />

    <rest-list-column
      prop="trigger_id"
      :title="$t('Action_Field_TriggerId')"
      :min-width="150"
      v-if="showTrigger"
    />

    <rest-list-column
      prop="profile_id"
      :title="$t('Action_Field_Profile')"
      :route="profileIdRoute"
      :width="300"
      v-if="showProfile"
    />

    <rest-list-column-actions v-if="$can('UserPersonalData::Read')">
      <template slot-scope="scope">
        <rest-list-action
          v-if="canCancel(scope.row)"
          :click="() => cancel(scope.row)"
          :title="$t('NotificationLog_Action_Cancel')"
          icon="el-icon-circle-close"
        />

        <rest-list-action-view :route="rowViewRoute(scope.row)" />

        <rest-list-action-download
          v-if="canDownload(scope.row) && $can('UserPersonalData::Export')"
          redirect-route-name="TaskHistoryView"
          :endpoint="rowDownloadEndpoint(scope.row)"
        />

        <rest-list-action-delete
          v-if="$isRole('admin')"
          :endpoint="rowDeleteEndpoint(scope.row)"
        />
      </template>
    </rest-list-column-actions>
  </rest-list>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import RestListColumnNumber from '@/common/components/rest/RestListColumnNumber'
import RestListActionView from '@/common/components/rest/RestListActionView'
import RestListActionDownload from '@/common/components/rest/RestListActionDownload'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListAction from '@/common/components/rest/RestListAction'
import { projectEndpoint, projectRoute, formatNumber } from '@/core/helpers'
import { MessageTypeLabel } from '@/common/enum/MessageTypeEnum'
import { PushEnvironmentLabel } from '@/common/enum/PushEnvironmentEnum'
import { PushActionErrorLabel } from '@/common/enum/PushActionErrorEnum'
import {
  ActionStatusLabel,
  ActionStatusEnum
} from '@/common/enum/ActionStatusEnum'
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'

export default {
  name: 'NotificationList',

  components: {
    RestListColumnActions,
    RestListColumn,
    RestList,
    RestListColumnEnum,
    RestListColumnDate,
    RestListColumnNumber,
    RestListActionView,
    RestListActionDownload,
    RestListActionDelete,
    RestListAction
  },

  data() {
    return {
      applications: [],
      MessageTypeLabel,
      PushEnvironmentLabel,
      ActionStatusLabel,
      ActionStatusEnum
    }
  },

  props: {
    campaignId: {
      required: false
    },
    showProfile: {
      default: false
    },
    showTrigger: {
      default: false
    }
  },

  computed: {
    listEndpoint() {
      if (this.campaignId) {
        return projectEndpoint({
          method: 'get',
          url: `actions`,
          params: {
            campaign_id: this.campaignId,
            sort: '-create_time',
            expand: this.showProfile ? 'profile_id' : null
          }
        })
      } else {
        return projectEndpoint({
          method: 'get',
          url: `actions`,
          params: {
            expand: this.showProfile
              ? 'profile_id,campaign_title'
              : 'campaign_title',
            sort: '-create_time'
          }
        })
      }
    },

    applicationsList() {
      let list = {}
      this.applications.forEach(app => {
        list[app.id] = app.name
      })
      return list
    }
  },

  created() {
    RestApi({
      method: 'get',
      url: `/projects/${this.$route.query.project_id}`
    }).then(({ data }) => {
      this.applications = data.applications.map(item => {
        return { id: item.id, name: item.name }
      })
    })
  },

  methods: {
    refresh(silent) {
      this.$refs.list.refresh(false, silent)
    },

    profileIdRoute(row) {
      if (row.profile_id && this.$can('Profile::Read')) {
        return projectRoute({
          name: 'ProfileOverview',
          query: {
            profile_id: row.profile_id
          }
        })
      } else {
        return null
      }
    },

    rowViewRoute(row) {
      return projectRoute({
        name: 'NotificationDetails',
        query: { id: row.id }
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `actions/${row.id}`
      })
    },

    rowDownloadEndpoint(row) {
      return projectEndpoint({
        method: 'get',
        url: `actions/${row.id}/export`
      })
    },

    targetCountFormatter(value, row) {
      return row.status === ActionStatusEnum.PREPARING
        ? `> ${formatNumber(value)}`
        : formatNumber(value)
    },

    errorClass(row) {
      return row.sent_count > 0 ? 'action-warning' : 'action-error'
    },

    errorMessage(row) {
      return row.error_message in PushActionErrorLabel
        ? this.$t(PushActionErrorLabel[row.error_message])
        : row.error_message
    },

    canCancel(row) {
      return [ActionStatusEnum.PREPARING, ActionStatusEnum.PROCESSING].includes(
        row.status
      )
    },

    canDownload(row) {
      return [ActionStatusEnum.SENT, ActionStatusEnum.CANCELLED].includes(
        row.status
      )
    },

    cancel(row) {
      this.$msgbox({
        title: this.$t('NotificationLog_CancelDialog_Title'),
        message: this.$t('NotificationLog_CancelDialog_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('NotificationLog_CancelDialog_Confirm'),
        cancelButtonText: this.$t('NotificationLog_CancelDialog_Cancel'),
        center: true
      }).then(() => {
        this.$refs.list.loading = true
        RestApi(
          projectEndpoint({
            method: 'post',
            url: `actions/${row.id}/cancel`
          })
        )
          .then(() => {
            this.$message({
              message: this.$t('NotificationLog_CancelDialog_SuccessMessage'),
              type: 'success'
            })
            this.$refs.list.refresh()
          })
          .catch(error => {
            this.$refs.list.loading = false
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      })
    }
  }
}
</script>

<style>
.action-error i.el-icon-warning {
  color: #f56c6c;
}
.action-warning i.el-icon-warning {
  color: #e6a23c;
}
td.is-right {
  padding-right: 25px;
}
</style>



// WEBPACK FOOTER //
// src/modules/notifications-log/components/NotificationList.vue